import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { useAuthContext } from "../../../AuthContextWrapper";
import { Estate } from "../../../models/Estate";
import { OUTCOMES } from "../../../constants";
import {
  AttorneyIcon,
  BeneficiariesIcon,
  CoExecutorIcon,
  CpaIcon,
  FinancialAdvisorIcon,
  FuneralHomeIcon,
  InsuranceAdvisorIcon,
  ProbateClerkIcon,
  RealEstateAgentIcon,
  WorkingAloneIcon,
} from "../shared/LineIcons";
import {
  MultiSelectButtonForm,
  SelectButton,
} from "../shared/MultiSelectButtonForm";
import { enumToStringDictionary } from "@theatticusapp/atticus-shared-components";

export enum Outcomes {
  coExecutor = "Another executor (co-executor)",
  beneficiaries = "One or more beneficiaries",
  attorney = "An attorney",
  funeralHome = "Funeral home",
  financialAdvisor = "Financial Advisor",
  cpa = "CPA",
  insuranceAdvisor = "Insurance advisor",
  probateClerk = "Probate clerk",
  realEstateAgent = "Real estate agent",
  alone = "I’m working on this alone",
}

const iconStyles = { width: 60, height: 60 } as const;
const iconViewBox = "0 0 60 60";

const icons: { [key: string]: JSX.Element } = {
  coExecutor: <CoExecutorIcon sx={iconStyles} viewBox={iconViewBox} />,
  beneficiaries: <BeneficiariesIcon sx={iconStyles} viewBox={iconViewBox} />,
  attorney: <AttorneyIcon sx={iconStyles} viewBox={iconViewBox} />,
  funeralHome: <FuneralHomeIcon sx={iconStyles} viewBox={iconViewBox} />,
  financialAdvisor: (
    <FinancialAdvisorIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  cpa: <CpaIcon sx={iconStyles} viewBox={iconViewBox} />,
  insuranceAdvisor: (
    <InsuranceAdvisorIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  probateClerk: <ProbateClerkIcon sx={iconStyles} viewBox={iconViewBox} />,
  realEstateAgent: (
    <RealEstateAgentIcon sx={iconStyles} viewBox={iconViewBox} />
  ),
  alone: <WorkingAloneIcon sx={iconStyles} viewBox={iconViewBox} />,
};

const outcomesDictionary = enumToStringDictionary(Outcomes);
const teamSelectButtons: SelectButton[] = [];
Object.keys(outcomesDictionary).forEach((key: string) =>
  teamSelectButtons.push({
    key,
    text: outcomesDictionary[key],
    icon: icons[key],
  })
);

export const EstateTeamScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, setActiveEstate } = useAuthContext()
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (checked: string[]): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    // const estate = activeEstate ? Estate.from(activeEstate) : new Estate();
    const estate = activeEstate || new Estate();
    // console.log("checked", checked);
    estate.estateTeam = checked;

    try {
      await estate.saveEstate();
      setActiveEstate(estate);
    } catch (error) {
      throw new Error("could not save data");
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper title="It takes a village—who else is helping you with this process?">
      <MultiSelectButtonForm
        initChecked={activeEstate?.estateTeam}
        selectButtons={teamSelectButtons}
        isOneRequired={true}
        noneOfAboveButtonKey="alone"
        onComplete={handleComplete}
        onBack={() => navigate(-1)}
      />
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
